import React from 'react';
import { Provider } from 'react-redux';
import PropTypes from 'prop-types';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { betslipStore } from '~Betslip/store/store';
import { closeModal } from '~Betslip/slices/uiSlice';
import { selectModalDetails } from '~Betslip/selectors/modalSelectors';
import {
    selectAllSelectionsAreSuspended,
} from '~Betslip/selectors/selectionDetailsSelectors';
import { getGeoErrorContactUsUrl } from '~Betslip/utils/errorMappings';

import {
    placeBet,
} from '~Betslip/thunks/placeBet';

import { calculateAmericanPrice } from '~shared/components/sport-event/utils';
import { getOddsFormat } from '~shared/utils/user';
import { decimalPriceOdds } from '~shared/utils/selections';
import AcceptOddsChanges from '~Betslip/uiModes/Full/Components/AcceptOddsChanges';

import { DATE_TIME_FORMAT_HM } from '~shared/utils/constants';
import { getLocalizedDateFormat } from '~shared/utils/date-time';
import Time from '~shared/ui/Time/Time';
import { Trans } from 'react-i18next';
import {
    kickUser,
    navigateToExternalLink,
    openDepositWidget,
} from '~spa/Utils/WrapperIntegration/Ucf';

import './styles.scss';

const buttonClasses = classNames([
    'fullslip__cta', 'bsmodal__button',
]);

const BackButton = ({ isSecondary }) => {
    const dispatch = useDispatch();
    const [ t ] = useTranslation();

    const onClose = () => {
        dispatch(closeModal());
    };

    const backButtonClasses = classNames(buttonClasses, {
        'bsmodal__secondary-back': isSecondary,
    });

    return <div className="bsmodal__dismiss-cta">
        <button
            className={backButtonClasses}
            onClick={onClose}
        >
            { t('betslip.back_to_betslip') }
        </button>
    </div>;
};

BackButton.propTypes = {
    isSecondary: PropTypes.bool,
    isBetslipLimit: PropTypes.bool,
};

BackButton.defaultProps = {
    isSecondary: false,
    isBetslipLimit: false,
};

const SuspendedModal = ({ details }) => {
    const dispatch = useDispatch();
    const allSelectionsAreSuspended = useSelector(selectAllSelectionsAreSuspended);

    const [ t ] = useTranslation();
    const { selections, isMultibet } = details;

    const placeTheNonSuspendedOnes = () => {
        dispatch(placeBet());
        dispatch(closeModal());
    };

    const renderSuspendedList = () => {
        const suspendedSelections = selections.map(
            ({ id, selection_name }) => (
                <li
                    key={id}
                    className="bsmodal__involved-selection"
                >
                    { selection_name }
                </li>
            ),
        );

        return <ul className="bsmodal__involved-selections">
            { suspendedSelections }
        </ul>;
    };

    const renderWarningMessage = () => {
        if (isMultibet) {
            return <div className="bsmodal__warning-message">
                { t('betslip.multi_bet_unavailable') }
            </div>;
        }

        const suspendedMessage = allSelectionsAreSuspended
            ? t('betslip.selection_unavailable', { count: selections.length })
            : t('betslip.continue_w_remain_selections');

        return <div className="bsmodal__warning-message">
            { suspendedMessage }
        </div>;
    };

    const renderSuspendedCTAs = () => {
        const betOnNonSuspendedClasses = classNames([
            buttonClasses, 'bsmodal__suspended-extra-cta',
        ]);

        const showYesGetOn = !isMultibet && !allSelectionsAreSuspended;

        const yesGetOnButton = showYesGetOn
        ?
            <div className="bsmodal__suspended-ctas">
                <button
                    className={betOnNonSuspendedClasses}
                    onClick={placeTheNonSuspendedOnes}>
                    { t('betslip.yes_get_on') }
                </button>
            </div>
        : null;

        return <>
            { yesGetOnButton }
            <BackButton isSecondary={showYesGetOn}/>
        </>;
    };

    return <>
        { renderSuspendedList() }
        { renderWarningMessage() }
        { renderSuspendedCTAs() }
    </>;
};

SuspendedModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const OddsChangeModal = ({ details }) => {
    const [ t ] = useTranslation();
    const dispatch = useDispatch();
    const priceChangeSelections = details?.bets?.[0]?.data?.selections;

    const renderOddsChangeList = () => {
        const oddsSelections = priceChangeSelections.map(
            ({ id, ...prices }) => {
                const selection = details?.selections?.[id];
                const oddsFormat = getOddsFormat();
                let oldPrice = null;
                let newPrice = null;

                switch (oddsFormat) {
                    case 'AMERICAN':
                        oldPrice = calculateAmericanPrice(prices.old_price_decimal);
                        newPrice = calculateAmericanPrice(prices.new_price_decimal);
                        break;
                    case 'FRACTION':
                        oldPrice = prices.old_price_fraction;
                        newPrice = prices.new_price_fraction;
                        break;
                    default:
                        oldPrice = decimalPriceOdds(prices.old_price_decimal);
                        newPrice = decimalPriceOdds(prices.new_price_decimal);
                        break;
                }

                return (
                    <li key={id} className="bsmodal__price-change">
                        <div className="bsmodal__price-change-selection">{selection.selection_name}</div>
                        <div>
                            <span className="bsmodal__old-price">{oldPrice}</span>
                            <span>{'>'}</span>
                            <span className="bsmodal__new-price">{newPrice}</span>
                        </div>
                    </li>
                );

            }
        );

        return <ul className="bsmodal__involved-selections">
            { oddsSelections }
        </ul>;
    };

    const onPlaceBet = () => {
        dispatch(placeBet());
        dispatch(closeModal());
    };

    const onClose = () => {
        dispatch(closeModal());
    };

    const renderCTAs = () => {

        return (
            <div className="bsmodal__price-change-ctas">
                <div
                    className="bsmodal__button bsmodal__odds-back-betslip"
                    onClick={onClose}
                >
                    { t('betslip.back_to_betslip') }
                </div>
                <div
                    className="bsmodal__button bsmodal__odds-place-bet"
                    onClick={onPlaceBet}>
                    { t('betslip.place_bet') }
                </div>
            </div>
        );
    };

    return <>
        { renderOddsChangeList() }
        { renderCTAs() }
        <AcceptOddsChanges source="new-price-movement"/>
    </>;
};

OddsChangeModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const BasicModal = ({ details }) => {
    const textClasses = classNames('bsmodal__text', {
        'bsmodal__text-block': details.type === 'spain_inplay_wagering',
    });

    return <>
        <span className={textClasses}>
            <Trans
                values={details.translationVariables}
            >
                {details.content}
            </Trans>
        </span>
        <BackButton />
    </>;
};

BasicModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const GeoModal = ({ details }) => {
    const [ t ] = useTranslation();
    const dispatch = useDispatch();

    const onClickLogout = () => {
        kickUser();
    };

    const onClickContact = () => {
        dispatch(closeModal());
        navigateToExternalLink(getGeoErrorContactUsUrl());
    };

    return <>
        <span className="bsmodal__text">{t(details.content)}</span>
        <div className="bsmodal__multi-errors-ctas">
            <div
                className="bsmodal__button bsmodal__multi-primary"
                onClick={onClickLogout}
            >
                { t('betslip.logout_and_retry') }
            </div>
            <div
                className="bsmodal__button bsmodal__secondary-back"
                onClick={onClickContact}>
                { t('betslip.contact_us') }
            </div>
        </div>
    </>;
};

GeoModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const GeoEventModal = ({ details }) => {
    const [t] = useTranslation();
    const {
        content,
        bets,
        selectionDetails,
    } = details;

    const renderMessage = () => {
        const errorBets = bets.reduce((acc, currentSelection) => {
            const {
                error: {
                    id: errorId,
                    info: {
                        failed_selections: failedSelections = [],
                    } = {},
                },
            } = currentSelection;

            if (errorId === 'GEO_EVENT_ERROR') {
                failedSelections.forEach(id => {
                    const selection = selectionDetails?.[id];
                    if (selection) {
                        acc[selection.event_id] = selection;
                    }
                });
            }

            return acc;
        }, {});

        const selections = Object.values(errorBets);
        const eventNames = selections.map((
            {
                scheduled_start: scheduledStart,
                event_name: eventName,
            }, index) => {
                return <>
                    <Time dateTime={scheduledStart} format={getLocalizedDateFormat(DATE_TIME_FORMAT_HM)} />
                    {` ${eventName}${index < (selections.length - 1) ? ', ' : ''}`}
                </>;
        });

        return <>
            <div>{t(content, { count: selections.length })}</div>
            <div>{eventNames}</div>
        </>;
    };

    return <>
        <div className="bsmodal__text">
            {renderMessage()}
        </div>
        <div className="bsmodal__multi-errors-ctas">
            <BackButton />
        </div>
    </>;
};

GeoEventModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const InsufficientFundsModal = ({ details }) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();

    const onClickBack = () => {
        dispatch(closeModal());
    };

    const onClickDeposit = () => {
        openDepositWidget();
        dispatch(closeModal());
    };

    return <>
        <span className="bsmodal__text">
            <Trans
                tOptions={{ interpolation: { escapeValue: true } }}
            >
                {details.content}
            </Trans>
        </span>

        <div className="bsmodal__multi-errors-ctas">
            <div
                className="bsmodal__button bsmodal__multi-primary"
                data-spectate-product="insufficient-funds;deposit"
                onClick={onClickDeposit}
            >
                { t('betslip.deposit') }
            </div>
            <div
                className="bsmodal__button bsmodal__secondary-back"
                data-spectate-product="insufficient-funds;back-to-betslip"
                onClick={onClickBack}
            >
                { t('betslip.back') }
            </div>
        </div>
    </>;
};

InsufficientFundsModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const TimeLimitModal = ({ details }) => {
    const [ t ] = useTranslation();

    const onClickLogout = () => {
        kickUser();
    };

    return <>
        <span className="bsmodal__text">{t(details.content)}</span>
        <div className="bsmodal__multi-errors-ctas">
            <div
                className="bsmodal__button bsmodal__multi-primary"
                onClick={onClickLogout}
            >
                { t('betslip.logout') }
            </div>
            <BackButton isSecondary={true}/>
        </div>
    </>;
};

TimeLimitModal.propTypes = {
    details: PropTypes.object.isRequired,
};

const GeneralModal = () => {
    const [ t ] = useTranslation();

    return <>
        <span className="bsmodal__text">
            { t('betslip.try_later') }
        </span>
        <BackButton />
    </>;
};

const ModalFrame = ({ children, title }) => {
    const [ t ] = useTranslation();

    return (
        <div
            className="bsmodal">
            <h2 className="bsmodal__title">
                { t(title) }
            </h2>
            <div className="bsmodal__body">
                <div className="bsmodal__error-icon">
                    <i className="bsmodal__alert-icon"></i>
                </div>
                { children }
            </div>
        </div>
    );
};

ModalFrame.propTypes = {
    children: PropTypes.element.isRequired,
    title: PropTypes.string.isRequired,
};

const getComponentToUse = (type) => {
    switch (type) {
        case 'suspended':
            return SuspendedModal;
        case 'price_changed':
            return OddsChangeModal;
        case 'insufficient_funds':
            return InsufficientFundsModal;
        case 'bet_limits':
        case 'loss_limits':
        case 'spain_inplay_wagering':
        case 'betslip_limit':
            return BasicModal;
        case 'geo_error':
            return GeoModal;
        case 'geo_event_error':
            return GeoEventModal;
        case 'time_limit_reached':
            return TimeLimitModal;
        default:
            return GeneralModal;
    }
};

const BetslipModal = () => {
    const modalDetails = useSelector(selectModalDetails);

    if (!modalDetails) {
        return null;
    }

    const {
        title,
    } = modalDetails;

    const InnerModal = getComponentToUse(modalDetails.type);

    return (
        <ModalFrame title={title}>
            <InnerModal
                details={modalDetails}
            />
        </ModalFrame>
    );
};

const BetslipModalWrapper = () => {
    return <Provider store={betslipStore}>
        <BetslipModal />
    </Provider>;
};

export default BetslipModalWrapper;
